import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import localDatabase from '../services/localDatabase';
import { subscribeCrossTab } from '../services/crossTab';
import { useTenant } from './TenantContext';
import { useAuth } from './AuthContext';
import type { Customer, Reservation, Table } from '../types/reservation';
import {
  compareByNewest,
  createNotificationEvent,
  getEventTypeFromReservationChange,
  inferNotificationSource,
  type NotificationEvent,
  type NotificationEventType,
  type NotificationSource,
} from '../lib/notifications';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { supabase } from '../lib/supabase';

// ---------------------------------------------------------------------------
// Feature flag: set VITE_CANONICAL_NOTIFICATIONS=true in your .env.local to
// enable the new server-backed canonical notifications system.
// ---------------------------------------------------------------------------
const USE_CANONICAL = import.meta.env.VITE_CANONICAL_NOTIFICATIONS === 'true';


const MAX_NOTIFICATIONS = 200;
const MAX_CANONICAL = 100;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Shared context shape
// ---------------------------------------------------------------------------
interface NotificationsContextValue {
  notifications: NotificationEvent[];
  readIds: Set<string>;
  unreadCount: number;
  soundEnabled: boolean;
  loading: boolean;
  /** true while showing cached data (offline or server fetch failed) */
  isStale: boolean;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: (ids?: string[]) => void;
  setSoundEnabled: (enabled: boolean) => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Shared key helpers
// ---------------------------------------------------------------------------
function getReadKey(tenantId: string | null) {
  return `notifications_read_ids_${tenantId ?? 'default'}`;
}
function getSoundKey(tenantId: string | null) {
  return `notifications_sound_enabled_${tenantId ?? 'default'}`;
}
function getCanonicalCacheKey(tenantId: string) {
  return `notifications_canonical_cache_${tenantId}`;
}

// ---------------------------------------------------------------------------
// Helper: map a Supabase notifications row to NotificationEvent
// ---------------------------------------------------------------------------
function mapDbRowToEvent(row: Record<string, unknown>): NotificationEvent {
  return {
    id: row.id as string,
    reservationId: row.reservation_id as string,
    type: row.type as NotificationEventType,
    source: row.source as NotificationSource,
    shiftId: (row.shift_id as string | null) ?? undefined,
    notes: (row.notes as string | null) ?? undefined,
    timestamp: row.timestamp as string,
    title: row.title as string,
    description: row.description as string,
    customerName: row.customer_name as string,
    partySize: row.party_size as number,
    reservationDate: row.reservation_date as string,
    reservationTime: row.reservation_time as string,
    tableLabel: row.table_label as string,
  };
}

// ===========================================================================
// CANONICAL PROVIDER
// Reads from and subscribes to the `public.notifications` Supabase table.
// ===========================================================================
function CanonicalNotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const { playSoftDing } = useNotificationSound();

  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [isStale, setIsStale] = useState<boolean>(false);

  // Tracks whether notification_reads cloud table is reachable this session
  const cloudAvailableRef = useRef<'unknown' | true | false>('unknown');

  // Reset availability probe on tenant/user change
  useEffect(() => {
    cloudAvailableRef.current = 'unknown';
  }, [tenantId, user?.id]);

  // -------------------------------------------------------------------------
  // Read-IDs persistence helpers (same as legacy)
  // -------------------------------------------------------------------------
  const persistReadIds = useCallback(
    (nextSet: Set<string>) => {
      try {
        localStorage.setItem(getReadKey(tenantId), JSON.stringify(Array.from(nextSet)));
      } catch {}
    },
    [tenantId]
  );

  // -------------------------------------------------------------------------
  // Notification cache (localStorage) for offline read-only support
  // -------------------------------------------------------------------------
  const loadCachedNotifications = useCallback((): NotificationEvent[] => {
    if (!tenantId) return [];
    try {
      const raw = localStorage.getItem(getCanonicalCacheKey(tenantId));
      if (!raw) return [];
      return JSON.parse(raw) as NotificationEvent[];
    } catch {
      return [];
    }
  }, [tenantId]);

  const persistCachedNotifications = useCallback(
    (events: NotificationEvent[]) => {
      if (!tenantId) return;
      try {
        localStorage.setItem(getCanonicalCacheKey(tenantId), JSON.stringify(events));
      } catch {}
    },
    [tenantId]
  );

  // -------------------------------------------------------------------------
  // Cloud read-IDs (notification_reads table)
  // -------------------------------------------------------------------------
  const loadCloudReadIds = useCallback(async (): Promise<Set<string> | null> => {
    if (!tenantId || !user?.id) return null;
    if (cloudAvailableRef.current === false) return null;

    const { data, error } = await supabase
      .from('notification_reads')
      .select('notification_id')
      .eq('tenant_id', tenantId)
      .eq('user_id', user.id);

    if (error) {
      if (cloudAvailableRef.current === 'unknown') {
        console.warn('[Notifications] Cloud read-state unavailable (local-only fallback):', error.message);
        cloudAvailableRef.current = false;
      }
      return null;
    }

    cloudAvailableRef.current = true;
    return new Set(
      (data ?? [])
        .map((row: Record<string, unknown>) => row.notification_id as string | null)
        .filter((v): v is string => Boolean(v))
    );
  }, [tenantId, user?.id]);

  const syncReadIdsToCloud = useCallback(
    async (ids: string[]) => {
      if (!tenantId || !user?.id || !ids.length) return;
      if (cloudAvailableRef.current === false) return;

      const payload = Array.from(new Set(ids)).map((notificationId) => ({
        tenant_id: tenantId,
        user_id: user.id,
        notification_id: notificationId,
        read_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('notification_reads')
        .upsert(payload, { onConflict: 'tenant_id,user_id,notification_id' });

      if (error) {
        if (cloudAvailableRef.current === 'unknown' || cloudAvailableRef.current === true) {
          console.warn('[Notifications] Failed to sync read-state to cloud:', error.message);
          cloudAvailableRef.current = false;
        }
      } else {
        cloudAvailableRef.current = true;
      }
    },
    [tenantId, user?.id]
  );

  const mergeCloudReadIds = useCallback(async () => {
    const cloudIds = await loadCloudReadIds();
    if (!cloudIds || cloudIds.size === 0) return;
    setReadIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      cloudIds.forEach((id) => {
        if (!next.has(id)) { next.add(id); changed = true; }
      });
      if (!changed) return prev;
      persistReadIds(next);
      return next;
    });
  }, [loadCloudReadIds, persistReadIds]);

  // -------------------------------------------------------------------------
  // Fetch notifications from Supabase
  // -------------------------------------------------------------------------
  const fetchFromServer = useCallback(async (): Promise<NotificationEvent[] | null> => {
    if (!tenantId) return null;
    const thirtyDaysAgo = new Date(Date.now() - THIRTY_DAYS_MS).toISOString();

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('tenant_id', tenantId)
      .gte('timestamp', thirtyDaysAgo)
      .order('timestamp', { ascending: false })
      .limit(MAX_CANONICAL);

    if (error) {
      console.warn('[Notifications] Server fetch failed:', error.message);
      return null;
    }

    return (data ?? []).map((row) => mapDbRowToEvent(row as Record<string, unknown>));
  }, [tenantId]);

  const refreshNotifications = useCallback(async () => {
    const events = await fetchFromServer();
    if (events !== null) {
      setNotifications(events);
      persistCachedNotifications(events);
      setIsStale(false);
    }
    setLoading(false);
  }, [fetchFromServer, persistCachedNotifications]);

  // -------------------------------------------------------------------------
  // Initialisation: load cache → load read-IDs → fetch from server
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantId) {
      setNotifications([]);
      setReadIds(new Set());
      setLoading(true);
      setIsStale(false);
      return;
    }

    let cancelled = false;

    const init = async () => {
      // Sound preference
      try {
        const rawSound = localStorage.getItem(getSoundKey(tenantId));
        if (!cancelled) setSoundEnabledState(rawSound == null ? true : rawSound === 'true');
      } catch {
        if (!cancelled) setSoundEnabledState(true);
      }

      // Read IDs from localStorage (instant)
      let localReadIds = new Set<string>();
      try {
        const raw = localStorage.getItem(getReadKey(tenantId));
        localReadIds = raw ? new Set(JSON.parse(raw) as string[]) : new Set();
      } catch {}
      if (!cancelled) setReadIds(localReadIds);

      // Show cached notifications immediately so UI is never blank
      const cached = loadCachedNotifications();
      if (!cancelled && cached.length > 0) {
        setNotifications(cached);
        setIsStale(true); // stale until server confirms
        setLoading(false);
      } else if (!cancelled) {
        setLoading(true);
      }

      // Merge cloud read-IDs in the background
      const cloudReadIds = await loadCloudReadIds();
      if (!cancelled && cloudReadIds && cloudReadIds.size > 0) {
        const merged = new Set([...localReadIds, ...cloudReadIds]);
        setReadIds(merged);
        persistReadIds(merged);
      }

      // Fetch fresh notifications from server
      const events = await fetchFromServer();
      if (!cancelled) {
        if (events !== null) {
          setNotifications(events);
          persistCachedNotifications(events);
          setIsStale(false);
        } else if (cached.length === 0) {
          // Nothing in cache and server failed — leave empty with stale flag
          setIsStale(true);
        }
        setLoading(false);
      }
    };

    void init();
    return () => { cancelled = true; };
  }, [
    fetchFromServer,
    loadCachedNotifications,
    loadCloudReadIds,
    persistCachedNotifications,
    persistReadIds,
    tenantId,
  ]);

  // -------------------------------------------------------------------------
  // Safety net: when the app/tab becomes visible again, refetch notifications
  // and merge cloud read-IDs. This covers the case where Realtime missed
  // inserts while the app was backgrounded or suspended.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshNotifications();
        void mergeCloudReadIds();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [mergeCloudReadIds, refreshNotifications]);

  // -------------------------------------------------------------------------
  // Realtime: new notifications from the server (INSERT events)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantId) return;

    const channel = supabase
      .channel(`notifications:${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          const event = mapDbRowToEvent(payload.new as Record<string, unknown>);
          setNotifications((prev) => {
            if (prev.some((e) => e.id === event.id)) return prev;
            const next = [event, ...prev].slice(0, MAX_CANONICAL);
            persistCachedNotifications(next);
            return next;
          });
          if (event.type === 'NEW_RESERVATION_ONLINE') {
            void playSoftDing();
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [persistCachedNotifications, playSoftDing, tenantId]);

  // -------------------------------------------------------------------------
  // Realtime: read-receipts from other devices (notification_reads INSERT)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantId || !user?.id) return;

    const channel = supabase
      .channel(`notification-reads:${tenantId}:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_reads',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const notificationId = (payload.new as Record<string, unknown>)?.notification_id as string | undefined;
          if (!notificationId) return;
          setReadIds((prev) => {
            if (prev.has(notificationId)) return prev;
            const next = new Set(prev);
            next.add(notificationId);
            persistReadIds(next);
            return next;
          });
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [persistReadIds, tenantId, user?.id]);

  // -------------------------------------------------------------------------
  // Reconnect: re-fetch when browser comes back online
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleOnline = () => void refreshNotifications();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [refreshNotifications]);

  // Periodic safety net:
  // - merge read IDs
  // - refresh notifications when visible/online in case Realtime drops silently
  useEffect(() => {
    if (!tenantId) return;
    const interval = setInterval(() => {
      void mergeCloudReadIds();
      if (document.visibilityState === 'visible' && navigator.onLine) {
        void refreshNotifications();
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [mergeCloudReadIds, refreshNotifications, tenantId]);

  // -------------------------------------------------------------------------
  // markAsRead / markAllAsRead / setSoundEnabled
  // -------------------------------------------------------------------------
  const markAsRead = useCallback(
    (notificationId: string) => {
      setReadIds((prev) => {
        if (prev.has(notificationId)) return prev;
        const next = new Set(prev);
        next.add(notificationId);
        persistReadIds(next);
        return next;
      });
      void syncReadIdsToCloud([notificationId]);
    },
    [persistReadIds, syncReadIdsToCloud]
  );

  const markAllAsRead = useCallback(
    (ids?: string[]) => {
      const targetIds = ids ?? notifications.map((e) => e.id);
      setReadIds((prev) => {
        const next = new Set(prev);
        targetIds.forEach((id) => next.add(id));
        persistReadIds(next);
        return next;
      });
      void syncReadIdsToCloud(targetIds);
    },
    [notifications, persistReadIds, syncReadIdsToCloud]
  );

  const setSoundEnabled = useCallback(
    (enabled: boolean) => {
      setSoundEnabledState(enabled);
      try { localStorage.setItem(getSoundKey(tenantId), String(enabled)); } catch {}
    },
    [tenantId]
  );

  const unreadCount = useMemo(
    () => notifications.reduce((n, e) => (readIds.has(e.id) ? n : n + 1), 0),
    [notifications, readIds]
  );

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      readIds,
      unreadCount,
      soundEnabled,
      loading,
      isStale,
      markAsRead,
      markAllAsRead,
      setSoundEnabled,
      refreshNotifications,
    }),
    [
      isStale,
      loading,
      markAllAsRead,
      markAsRead,
      notifications,
      readIds,
      refreshNotifications,
      setSoundEnabled,
      soundEnabled,
      unreadCount,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

// ===========================================================================
// LEGACY PROVIDER
// Derives notifications locally from IndexedDB reservations (original logic).
// ===========================================================================

function buildBootstrapEvents(
  reservations: Reservation[],
  tablesById: Map<string, Table>,
  customersById: Map<string, Customer>
): NotificationEvent[] {
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

  return reservations
    .filter((reservation) => {
      const timestamp = new Date(reservation.updated_at || reservation.created_at).getTime();
      return now - timestamp <= sevenDaysMs;
    })
    .map((reservation) => {
      let type: NotificationEvent['type'] = 'NEW_RESERVATION_MANUAL';
      if (reservation.status === 'cancelled') type = 'CANCELLATION';
      else if (reservation.status === 'no-show') type = 'NO_SHOW_FLAGGED';
      else if (inferNotificationSource(reservation) === 'online') type = 'NEW_RESERVATION_ONLINE';

      return createNotificationEvent({
        reservation,
        type,
        tablesById,
        customersById,
        timestamp: reservation.updated_at || reservation.created_at,
      });
    })
    .sort(compareByNewest)
    .slice(0, MAX_NOTIFICATIONS);
}

function LegacyNotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { tenantId, dbReady } = useTenant();
  const { playSoftDing } = useNotificationSound();
  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const previousReservationsRef = useRef<Map<string, Reservation>>(new Map());
  const initializedRef = useRef(false);
  const cloudAvailableRef = useRef<'unknown' | true | false>('unknown');

  const persistReadIds = useCallback(
    (nextSet: Set<string>) => {
      try {
        localStorage.setItem(getReadKey(tenantId), JSON.stringify(Array.from(nextSet)));
      } catch {}
    },
    [tenantId]
  );

  useEffect(() => {
    cloudAvailableRef.current = 'unknown';
  }, [tenantId, user?.id]);

  const loadCloudReadIds = useCallback(async (): Promise<Set<string> | null> => {
    if (!tenantId || !user?.id) return null;
    if (cloudAvailableRef.current === false) return null;

    const { data, error } = await supabase
      .from('notification_reads')
      .select('notification_id')
      .eq('tenant_id', tenantId)
      .eq('user_id', user.id);

    if (error) {
      if (cloudAvailableRef.current === 'unknown') {
        console.warn('[Notifications] Cloud read-state sync unavailable (will use local-only):', error.message);
        cloudAvailableRef.current = false;
      }
      return null;
    }

    cloudAvailableRef.current = true;
    return new Set(
      (data ?? [])
        .map((row: Record<string, unknown>) => row.notification_id as string | null)
        .filter((value): value is string => Boolean(value))
    );
  }, [tenantId, user?.id]);

  const syncReadIdsToCloud = useCallback(
    async (ids: string[]) => {
      if (!tenantId || !user?.id || !ids.length) return;
      if (cloudAvailableRef.current === false) return;

      const uniqueIds = Array.from(new Set(ids));
      const payload = uniqueIds.map((notificationId) => ({
        tenant_id: tenantId,
        user_id: user.id,
        notification_id: notificationId,
        read_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('notification_reads')
        .upsert(payload, { onConflict: 'tenant_id,user_id,notification_id' });

      if (error) {
        if (cloudAvailableRef.current === 'unknown' || cloudAvailableRef.current === true) {
          console.warn('[Notifications] Failed to sync notification read-state to cloud:', error.message);
          cloudAvailableRef.current = false;
        }
      } else {
        cloudAvailableRef.current = true;
      }
    },
    [tenantId, user?.id]
  );

  const mergeCloudReadIds = useCallback(
    async () => {
      const cloudReadIds = await loadCloudReadIds();
      if (!cloudReadIds || cloudReadIds.size === 0) return;

      setReadIds((prev) => {
        let changed = false;
        const next = new Set(prev);
        cloudReadIds.forEach((id) => {
          if (!next.has(id)) { next.add(id); changed = true; }
        });
        if (!changed) return prev;
        persistReadIds(next);
        return next;
      });
    },
    [loadCloudReadIds, persistReadIds]
  );

  const refreshNotifications = useCallback(async () => {
    if (!tenantId || !dbReady) return;
    let reservationsRaw: unknown;
    let customersRaw: unknown;
    let tablesRaw: unknown;

    try {
      [reservationsRaw, customersRaw, tablesRaw] = await Promise.all([
        localDatabase.getAllReservations(),
        localDatabase.getAllCustomers(),
        localDatabase.getAllTables(),
      ]);
    } catch (error) {
      if (error instanceof Error && error.message.includes('Database not initialized')) {
        return;
      }
      console.error('Failed to refresh notifications:', error);
      return;
    }

    const reservations = reservationsRaw as unknown as Reservation[];
    const customers = customersRaw as unknown as Customer[];
    const tables = tablesRaw as unknown as Table[];

    const customersById = new Map(customers.map((c) => [c.id, c]));
    const tablesById = new Map(tables.map((t) => [t.id, t]));
    const nextReservationMap = new Map(reservations.map((r) => [r.id, r]));

    if (!initializedRef.current) {
      setNotifications(buildBootstrapEvents(reservations, tablesById, customersById));
      previousReservationsRef.current = nextReservationMap;
      initializedRef.current = true;
      setLoading(false);
      return;
    }

    const previousMap = previousReservationsRef.current;
    const newEvents: NotificationEvent[] = [];

    for (const reservation of reservations) {
      const previous = previousMap.get(reservation.id);
      const type = getEventTypeFromReservationChange(previous, reservation);
      if (!type) continue;

      const event = createNotificationEvent({
        reservation,
        type,
        tablesById,
        customersById,
        timestamp: reservation.updated_at || reservation.created_at,
      });

      newEvents.push(event);
    }

    if (newEvents.length > 0) {
      setNotifications((prev) => {
        const ids = new Set(prev.map((item) => item.id));
        const appended = newEvents.filter((item) => !ids.has(item.id));
        if (!appended.length) return prev;
        return [...appended, ...prev].sort(compareByNewest).slice(0, MAX_NOTIFICATIONS);
      });

      const hasOnlineNewReservation = newEvents.some((e) => e.type === 'NEW_RESERVATION_ONLINE');
      if (hasOnlineNewReservation && soundEnabled) {
        void playSoftDing();
      }
    }

    previousReservationsRef.current = nextReservationMap;
    setLoading(false);
  }, [dbReady, playSoftDing, soundEnabled, tenantId]);

  useEffect(() => {
    if (!tenantId || !dbReady) {
      setNotifications([]);
      setReadIds(new Set());
      initializedRef.current = false;
      previousReservationsRef.current = new Map();
      setLoading(true);
      return;
    }

    let cancelled = false;

    const initializeState = async () => {
      let localReadIds = new Set<string>();
      try {
        const rawReadIds = localStorage.getItem(getReadKey(tenantId));
        const parsed = rawReadIds ? (JSON.parse(rawReadIds) as string[]) : [];
        localReadIds = new Set(parsed);
      } catch {
        localReadIds = new Set();
      }

      if (!cancelled) setReadIds(localReadIds);

      try {
        const rawSound = localStorage.getItem(getSoundKey(tenantId));
        if (!cancelled) setSoundEnabledState(rawSound == null ? true : rawSound === 'true');
      } catch {
        if (!cancelled) setSoundEnabledState(true);
      }

      const cloudReadIds = await loadCloudReadIds();
      if (!cancelled && cloudReadIds && cloudReadIds.size > 0) {
        const merged = new Set([...localReadIds, ...cloudReadIds]);
        setReadIds(merged);
        persistReadIds(merged);
      }

      initializedRef.current = false;
      previousReservationsRef.current = new Map();
      setLoading(true);
      await refreshNotifications();
    };

    void initializeState();
    return () => { cancelled = true; };
  }, [dbReady, loadCloudReadIds, persistReadIds, refreshNotifications, tenantId]);

  useEffect(() => {
    if (!tenantId || !dbReady) return;
    const interval = setInterval(() => {
      void refreshNotifications();
      void mergeCloudReadIds();
    }, 30_000);

    const unsubscribe = subscribeCrossTab((message) => {
      if (message.type === 'sync:reservations' || message.type === 'sync:all') {
        void refreshNotifications();
      }
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [dbReady, mergeCloudReadIds, refreshNotifications, tenantId]);

  // Realtime: read-receipts from other devices
  useEffect(() => {
    if (!tenantId || !user?.id) return;

    const channel = supabase
      .channel(`notification-reads:${tenantId}:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_reads',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const notificationId = (payload.new as Record<string, unknown>)?.notification_id as string | undefined;
          if (!notificationId) return;
          setReadIds((prev) => {
            if (prev.has(notificationId)) return prev;
            const next = new Set(prev);
            next.add(notificationId);
            persistReadIds(next);
            return next;
          });
        }
      )
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [persistReadIds, tenantId, user?.id]);

  const markAsRead = useCallback(
    (notificationId: string) => {
      setReadIds((prev) => {
        if (prev.has(notificationId)) return prev;
        const next = new Set(prev);
        next.add(notificationId);
        persistReadIds(next);
        return next;
      });
      void syncReadIdsToCloud([notificationId]);
    },
    [persistReadIds, syncReadIdsToCloud]
  );

  const markAllAsRead = useCallback(
    (ids?: string[]) => {
      const targetIds = ids ?? notifications.map((event) => event.id);
      setReadIds((prev) => {
        const next = new Set(prev);
        targetIds.forEach((id) => next.add(id));
        persistReadIds(next);
        return next;
      });
      void syncReadIdsToCloud(targetIds);
    },
    [notifications, persistReadIds, syncReadIdsToCloud]
  );

  const setSoundEnabled = useCallback(
    (enabled: boolean) => {
      setSoundEnabledState(enabled);
      try { localStorage.setItem(getSoundKey(tenantId), String(enabled)); } catch {}
    },
    [tenantId]
  );

  const unreadCount = useMemo(
    () => notifications.reduce((total, event) => (readIds.has(event.id) ? total : total + 1), 0),
    [notifications, readIds]
  );

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      readIds,
      unreadCount,
      soundEnabled,
      loading,
      isStale: false, // legacy mode is always live (derived from local DB)
      markAsRead,
      markAllAsRead,
      setSoundEnabled,
      refreshNotifications,
    }),
    [
      loading,
      markAllAsRead,
      markAsRead,
      notifications,
      readIds,
      refreshNotifications,
      setSoundEnabled,
      soundEnabled,
      unreadCount,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

// ===========================================================================
// Public export: switches between canonical and legacy based on feature flag
// ===========================================================================
export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  if (USE_CANONICAL) {
    return <CanonicalNotificationsProvider>{children}</CanonicalNotificationsProvider>;
  }
  return <LegacyNotificationsProvider>{children}</LegacyNotificationsProvider>;
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
}
